import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { getUniqueElements, mapAndFilterUnique } from '../../../utils/arrayUtils';
import MultiSelect from '../../formElements/MultiSelect';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './editParticipantStatuses.module.css';

const EditParticipantStatuses = ({
    status,
    toStatuses,
    onStatusChanged,
    participantStatusReasonDetails,
    errors,
    clearError
}) => {
    // LOCAL STATE
    const [selectedToStatusIds, setSelectedToStatusIds] = useState([]);
    const [selectedToStatuses, setSelectedToStatuses] = useState([]);

    // USEEFFECTS
    useEffect(() => {
        const toStatusDDLSelectedIds = status.toParticipantStatuses.map(
            (el) => el.participantStatusId
        );
        setSelectedToStatusIds(toStatusDDLSelectedIds);
        setSelectedToStatuses(toStatuses.filter((el) => toStatusDDLSelectedIds.includes(el.id)));
    }, [status.toParticipantStatuses, toStatuses]);

    // EVENT HANDLERS
    const onToStatusesChanged = (chosenIds) => {
        clearError(`toParticipantStatuses-${status.participantStatusId}`);
        if (getUniqueElements(chosenIds, selectedToStatusIds).length === 0) return;
        const idsToRemove = selectedToStatusIds.filter((el) => !chosenIds.includes(el));
        const idsToAdd = chosenIds.filter((el) => !selectedToStatusIds.includes(el));

        let updatedToParticipantStatuses = status.toParticipantStatuses;
        if (idsToRemove.length > 0) {
            updatedToParticipantStatuses = status.toParticipantStatuses.filter(
                (el) => !idsToRemove.includes(el.participantStatusId)
            );
        } else clearError(`accordion-${status.participantStatusId}`);
        if (idsToAdd.length > 0) {
            updatedToParticipantStatuses = [
                ...updatedToParticipantStatuses,
                ...idsToAdd.map((el) => ({
                    participantStatusId: el,
                    participantStatus: toStatuses.find((entry) => entry.id === el)?.name,
                    participantStatusReasons: []
                }))
            ];
        }
        onStatusChanged({ ...status, toParticipantStatuses: updatedToParticipantStatuses });
    };

    const onToStatusReasonsChange = (toStatusId, chosenReasonIds, role) => {
        const currentStatusReasons = status.toParticipantStatuses.find(
            (el) => el.participantStatusId === toStatusId
        );
        const newSelectionForRole = mapAndFilterUnique([...chosenReasonIds]).map((el) => ({
            participantStatusReasonId: el,
            selectableBy: role
        }));
        const otherRoleReasons = currentStatusReasons.participantStatusReasons.filter(
            (el) => el.selectableBy !== role
        );
        onStatusChanged({
            ...status,
            toParticipantStatuses: status.toParticipantStatuses.map((el) =>
                el.participantStatusId === toStatusId
                    ? {
                          ...el,
                          participantStatusReasons: [...newSelectionForRole, ...otherRoleReasons]
                      }
                    : el
            )
        });
    };

    // RENDER
    return (
        <div className={form.formWrapper}>
            <div className={form.leftColOnly}>
                <MultiSelect
                    id={`toStatuses-${status.participantStatusId}`}
                    key={`toStatuses-${status.participantStatusId}`}
                    label="To Statuses"
                    placeholder="To Statuses"
                    mandatory={true}
                    maxPills={10}
                    menuItems={toStatuses || []}
                    preSelectedIds={selectedToStatusIds}
                    preSelects={selectedToStatuses}
                    error={errors[`toParticipantStatuses-${status.participantStatusId}`]}
                    onChange={onToStatusesChanged}
                />
            </div>
            {status.toParticipantStatuses.map((el, index) => {
                const id = `${status.participantStatusId}_${el.participantStatusId}`;
                const toStatus = toStatuses.find((entry) => entry.id === el.participantStatusId);
                const selectedAdviserReasons = el.participantStatusReasons
                    .filter((el) => el.selectableBy === 'adviser')
                    .map((el) => el.participantStatusReasonId);
                const selectedManagerReasons = el.participantStatusReasons
                    .filter((el) => el.selectableBy === 'manager')
                    .map((el) => el.participantStatusReasonId);
                const selectedPRaPReasons = el.participantStatusReasons
                    .filter((el) => el.selectableBy === 'prap')
                    .map((el) => el.participantStatusReasonId);
                const selectedQualityReasons = el.participantStatusReasons
                    .filter((el) => el.selectableBy === 'quality')
                    .map((el) => el.participantStatusReasonId);

                if (!toStatus) return;
                return (
                    <div key={id}>
                        <div key={`toStatuses-${id}`} className={local.statusRow}>
                            <label id={`label-${id}`} htmlFor={id} className={`${form.formLabel}`}>
                                {toStatus.name}
                            </label>
                        </div>

                        <div className={form.form}>
                            <div className={form.formSection}>
                                <div className={form.formColumn}>
                                    <MultiSelect
                                        id={`adviserToStatusReasons-${id}`}
                                        label="Adviser To Status Reasons"
                                        placeholder="Adviser To Status Reasons"
                                        maxPills={10}
                                        menuItems={participantStatusReasonDetails}
                                        preSelectedIds={selectedAdviserReasons}
                                        preSelects={participantStatusReasonDetails.filter((el) =>
                                            selectedAdviserReasons.includes(el.id)
                                        )}
                                        onChange={(chosenReasonIds) =>
                                            onToStatusReasonsChange(
                                                toStatus.id,
                                                chosenReasonIds,
                                                'adviser'
                                            )
                                        }
                                    />
                                    <MultiSelect
                                        id={`managerToStatusReasons-${id}`}
                                        label="Manager To Status Reasons"
                                        placeholder="Manager To Status Reasons"
                                        maxPills={10}
                                        menuItems={participantStatusReasonDetails}
                                        preSelectedIds={selectedManagerReasons}
                                        preSelects={participantStatusReasonDetails.filter((el) =>
                                            selectedManagerReasons.includes(el.id)
                                        )}
                                        onChange={(chosenReasonIds) =>
                                            onToStatusReasonsChange(
                                                toStatus.id,
                                                chosenReasonIds,
                                                'manager'
                                            )
                                        }
                                    />
                                </div>

                                <div className={form.formColumn}>
                                    <MultiSelect
                                        id={`prapToStatusReasons-${id}`}
                                        label="PRaP To Status Reasons"
                                        placeholder="PRaP To Status Reasons"
                                        maxPills={10}
                                        menuItems={participantStatusReasonDetails}
                                        preSelectedIds={selectedPRaPReasons}
                                        preSelects={participantStatusReasonDetails.filter((el) =>
                                            selectedPRaPReasons.includes(el.id)
                                        )}
                                        onChange={(chosenReasonIds) =>
                                            onToStatusReasonsChange(
                                                toStatus.id,
                                                chosenReasonIds,
                                                'prap'
                                            )
                                        }
                                    />
                                    <MultiSelect
                                        id={`qualityToStatusReasons-${id}`}
                                        label="Quality To Status Reasons"
                                        placeholder="Quality To Status Reasons"
                                        maxPills={10}
                                        menuItems={participantStatusReasonDetails}
                                        preSelectedIds={selectedQualityReasons}
                                        preSelects={participantStatusReasonDetails.filter((el) =>
                                            selectedQualityReasons.includes(el.id)
                                        )}
                                        onChange={(chosenReasonIds) =>
                                            onToStatusReasonsChange(
                                                toStatus.id,
                                                chosenReasonIds,
                                                'quality'
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                        {index + 1 < status.toParticipantStatuses.length && (
                            <div className={app.divider} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

EditParticipantStatuses.propTypes = {
    status: PropTypes.shape({
        id: PropTypes.string,
        participantStatusId: PropTypes.string,
        active: PropTypes.bool,
        toParticipantStatuses: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                participantStatusId: PropTypes.string,
                selectableByBmAndUp: PropTypes.bool,
                selectableByPrapAndUp: PropTypes.bool,
                selectableByQualityAndUp: PropTypes.bool,
                participantStatusReasonIds: PropTypes.arrayOf(PropTypes.string)
            })
        )
    }),
    toStatuses: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            status: PropTypes.string
        })
    ),
    onStatusChanged: PropTypes.func,
    participantStatusReasonDetails: PropTypes.array,
    errors: PropTypes.object,
    clearError: PropTypes.func
};

export default EditParticipantStatuses;
